import React, { useMemo } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { useDispatch, useSelector } from 'react-redux';
import Tag from 'ui/components/atoms/Tag';
import Card from 'ui/components/atoms/Card';
import DiscountTag from 'ui/components/atoms/DiscountTag';
import ServiceTags from '../ServiceTags';
import { imagePlaceholderSmallCardList } from '../../../assets/images';
import { calculateDiscount } from '../../../helpers/utils';
import useIsElementVisible from 'ui/hooks/useIsElementVisible';
import eventClickCoupon from 'ui/lib/gtm/eventClickCoupon';
import convertMoney from '../../../helpers/utils/convertMoney';
import { useRouter } from '../../../hooks/useCustomRouter';
import { setServicePackageState } from '../../../store/servicePackage/actions';
import generateImageResizer from '../../../helpers/utils/generateImageResizer';
import { IBreadcrumbs } from '../Breadcrumbs';
import eventClickPackageServiceList from '../../../helpers/emarsys/package-service/eventClickPackageServiceList';

interface CardServiceProps {
  index: number;
  title: string;
  price: number;
  oldPrice: number;
  tag: { name: string; label: string }[];
  typeID: string;
  textHarga?: string;
  usp: Array<{ text: string; iconUrl: string; hide?: boolean }>;
  url: string;
  className?: string;
  tagClassName?: string;
  image?: { url: string; altText: string; format: string; imageType: string }[];
  handleCollectDataLayer: (data: {
    name: string;
    id: string;
    price: number;
    brand: string;
    category: string;
    variant: string;
    list: string;
    position: number;
    discountedPrice: number;
  }) => void;
  tabList: { name: string; path: string }[];
  code: string;
  isRelevantPackage: boolean;
  bodyTypeName: string;
  isForAccList?: boolean;
  isHompage?: boolean;
  category?: string;
  impressionData?: {
    title: string;
    carModels: string[];
    breadcrumbItems: IBreadcrumbs[];
    customerNo?: string;
  };
}

const CardService = ({
  index,
  title,
  price,
  oldPrice,
  tag,
  typeID,
  textHarga = 'Harga Mulai',
  usp,
  url,
  className = '',
  tagClassName = '',
  image,
  handleCollectDataLayer = () => {},
  tabList,
  code,
  isRelevantPackage,
  bodyTypeName,
  isForAccList = false,
  isHompage = false,
  category,
  impressionData,
}: CardServiceProps) => {
  const { isMobileScreen } = useSelector((state: any) => state.page);
  const router = useRouter();
  const dispatch = useDispatch();
  const ref = useIsElementVisible(
    () =>
      handleCollectDataLayer({
        name: title?.toLowerCase() || 'not available',
        id: code?.toLowerCase(),
        price: Math.floor(oldPrice || price || 0),
        brand: 'toyota',
        category: isForAccList
          ? 'aksesoris'
          : router?.asPath !== '/'
            ? tabList?.find((e) => e?.path?.includes(router?.asPath))?.name?.toLowerCase()
            : bodyTypeName?.toLowerCase() || 'not available',
        variant: category?.toLowerCase() || 'not available',
        list:
          typeID === 'coupon'
            ? 'kupon servis'
            : typeID === 'accessories'
              ? 'jual aksesoris mobil toyota'
              : typeID === 'paling baru'
                ? 'paling baru'
                : 'paket servis',
        position: index + 1,
        discountedPrice: oldPrice ? Math.floor(price - oldPrice || 0) : 0,
      }),
    [router.asPath, isMobileScreen],
    null,
    false,
    isMobileScreen ? '0px 0px -70px 0px' : null
  );

  const discountPercentage = useMemo(() => {
    return calculateDiscount(price, oldPrice);
  }, [price, oldPrice]);

  let typeChecking: string;
  if (typeID === 'coupon') {
    typeChecking = 'kupon servis';
  } else if (router.asPath === '/c/aksesoris') {
    typeChecking = 'jual aksesoris mobil toyota';
  } else if (typeID === 'paling baru') {
    typeChecking = 'paling baru';
  } else if (typeID === 'accessories') {
    typeChecking = 'aksesoris';
  } else {
    typeChecking = 'paket servis';
  }

  const PriceTag = isForAccList ? 'p' : 'h3';
  const AnchorTag = isForAccList ? 'a' : Link;

  return (
    <AnchorTag
      href={url}
      onClick={() => {
        if (typeID === 'package') {
          dispatch(
            setServicePackageState({
              field: 'isShowBaseModel',
              value: true,
            })
          );
        }
        sessionStorage.setItem(
          'keyCategory',
          router?.asPath !== '/'
            ? tabList?.find((e) => e?.path?.includes(router?.asPath))?.name?.toLowerCase()
            : 'semua'
        );
        eventClickCoupon(
          {
            name: title?.toLowerCase() || 'not available',
            id: code?.toLowerCase() || url?.split('/').pop()?.toLowerCase(),
            price: Math.floor(oldPrice || price || 0),
            brand: 'toyota',
            category: isForAccList
              ? 'aksesoris'
              : router?.asPath !== '/'
                ? tabList?.find((e) => e?.path?.includes(router?.asPath))?.name?.toLowerCase()
                : bodyTypeName?.toLowerCase() || 'not available',
            variant: category?.toLowerCase() || 'not available',
            position: index + 1,
            discountedPrice: oldPrice ? Math.floor(price - oldPrice || 0) : 0,
          },
          typeChecking,
          isForAccList
            ? `aksesoris - ${category?.toLowerCase() || 'not available'}`
            : router?.asPath !== '/'
              ? tabList?.find((e) => e?.path?.includes(router?.asPath))?.name?.toLowerCase()
              : bodyTypeName?.toLowerCase() || 'not available',
          category
        );
      }}
    >
      <Card
        ref={ref}
        idCard={typeID && title ? `card-${typeID}-${title?.replace(/ /g, '-')}` : null}
        key={index}
        className={`h-full border border-reliableBlack5 flex flex-col lg:border-reliableBlack10 ${className}`}
      >
        {/* Card tags */}
        <div className={`${isRelevantPackage || oldPrice ? 'pb-0' : 'pb-8'}`}>
          {isRelevantPackage && (
            <Tag className={`rounded-br ${tagClassName}`} severity={'info'}>
              <span className="text-reliableBlack">Paling Relevan</span>
            </Tag>
          )}

          {oldPrice && (
            <Tag className={`rounded-br ${tagClassName}`} severity={'secondary'}>
              <span className="text-reliableBlack">On Sale</span>
            </Tag>
          )}
          {tag.map((item, idx) => {
            return (
              <React.Fragment key={idx}>
                <Tag
                  className={`rounded-br ${tagClassName}`}
                  severity={item.name === 'relevan' ? 'info' : 'secondary'}
                >
                  <span className="text-reliableBlack">{item.label}</span>
                </Tag>
              </React.Fragment>
            );
          })}
        </div>

        <div className="pt-[6px] px-4 pb-4 lg:pt-2 flex flex-col">
          {/* Card image */}
          <div className="relative mb-2 overflow-x-hidden lg:mb-3">
            {image?.length > 0 ? (
              image
                ?.filter((item) => item?.format === 'thumbnail' && item?.imageType === 'PRIMARY')
                .map((item, idx) => (
                  <Image
                    key={idx}
                    alt={item?.altText}
                    width={256}
                    height={256}
                    className="aspect-video object-cover object-center"
                    src={generateImageResizer(item?.url, 256, 'webp')}
                    loading={`${idx < 3 ? 'eager' : 'lazy'}`}
                    quality={50}
                  />
                ))
            ) : (
              <Image
                src={imagePlaceholderSmallCardList.src}
                alt={'thumbnail'}
                width={256}
                height={256}
                className="aspect-video object-cover object-center"
                quality={50}
              />
            )}
            {isForAccList ? (
              <ServiceTags
                width={160}
                className="absolute bottom-0 fit-content"
                size="medium"
                items={usp}
                isForAccList={isForAccList}
                isHompage={isHompage}
              />
            ) : (
              <ServiceTags width={80} className="absolute bottom-0 " size="medium" items={usp} />
            )}
          </div>

          {/* Card Content */}
          {isForAccList ? (
            <h3 className="text-sm font-semibold text-reliableBlack line-clamp-2 leading-tight mb-1 lg:text-lg lg:font-bold lg:mb-3 h-[32px] lg:h-[55px]">
              {title}
            </h3>
          ) : (
            <h2 className="text-sm font-semibold text-reliableBlack line-clamp-2 leading-tight mb-1 lg:text-lg lg:font-bold lg:mb-3 h-[32px] lg:h-[55px]">
              {title}
            </h2>
          )}

          {price && (
            <div>
              <p className="text-[10px] font-medium text-reliableBlack70 mb-0.5 lg:text-sm lg:font-normal lg:mb-1">
                {textHarga}
              </p>
              <div className="flex items-center mb-1">
                <PriceTag className="text-sm font-semibold text-supportiveRed mr-1.5 lg:text-lg lg:mr-2">
                  {oldPrice ? convertMoney(oldPrice, true) : convertMoney(price, true)}
                </PriceTag>

                {oldPrice && (
                  <DiscountTag
                    classNames={`px-1 py-0 lg:px-1 lg:py-0.5  ${isForAccList && discountPercentage != null ? 'bg-[#bdf2f0]' : ''}`}
                  >
                    <span
                      className={`text-xs font-normal text-reliableBlack lg:text-base lg:font-medium`}
                    >
                      {discountPercentage}
                    </span>
                  </DiscountTag>
                )}
              </div>

              {oldPrice && (
                <p
                  className={`text-[10px] text-reliableBlack70 font-medium line-through lg:text-base `}
                >
                  {convertMoney(price, true)}
                </p>
              )}
            </div>
          )}
        </div>
      </Card>
    </AnchorTag>
  );
};

export default CardService;
